import type { Timestamp } from "firebase/firestore";
import type { WhatsappThread } from "../../types";

// Pure helpers for the inbox thread list, kept out of the component for testing.

export type InboxView = "conversations" | "all";

const millis = (t?: Timestamp) => t?.toMillis?.() ?? 0;

/** Most recent activity on the thread: a chat message or a template send, whichever is later. */
export const threadActivityAt = (thread: WhatsappThread): Timestamp | undefined => {
  if (!thread.lastTemplateAt) return thread.lastMessageAt;
  if (!thread.lastMessageAt) return thread.lastTemplateAt;
  return millis(thread.lastTemplateAt) > millis(thread.lastMessageAt) ? thread.lastTemplateAt : thread.lastMessageAt;
};

/** Merges the chat-ordered and template-ordered queries (they overlap) into one list, latest activity first. */
export const mergeThreadLists = (...lists: WhatsappThread[][]): WhatsappThread[] => {
  const byId = new Map<string, WhatsappThread>();
  lists.flat().forEach((thread) => {
    if (thread.id) byId.set(thread.id, thread);
  });
  return [...byId.values()].sort((a, b) => millis(threadActivityAt(b)) - millis(threadActivityAt(a)));
};

/** Matches the client's names, WhatsApp profile name, or number (digits compared for numbers). */
export const threadMatchesFilter = (thread: WhatsappThread, filter: string): boolean => {
  const needle = filter.trim().toLowerCase();
  if (!needle) return true;
  const digits = needle.replace(/\D/g, "");
  if (digits && (thread.mobile ?? "").replace(/\D/g, "").includes(digits)) return true;
  const haystack = [thread.profileName, ...(thread.customerNames ?? []).flatMap((n) => [n.name_en, n.name_ar])]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
};

/** Row snippet: the last chat message, unless a template send is more recent. */
export const threadSnippet = (thread: WhatsappThread): { text: string; template: boolean; outbound: boolean } => {
  const templateIsLatest = !!thread.lastTemplateAt
    && (!thread.lastMessageAt || millis(thread.lastTemplateAt) > millis(thread.lastMessageAt));
  if (templateIsLatest) {
    return { text: thread.lastTemplateBody || thread.lastTemplateName || "Template sent", template: true, outbound: true };
  }
  return { text: thread.lastMessageBody || "…", template: false, outbound: thread.lastMessageDirection === "outbound" };
};
